import { useContext } from 'react';

import { Box, Button, Divider, Link, Paper, Typography } from '@mui/material';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import { useTheme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import ZendeskLoader from 'src/components/Zendesk/ZendeskLoader';
import ConfigContext from 'src/context/config';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';
import Logo from '../LayoutV2/header/logo';

interface Props {
  // prop used to determine if the cart sticky footer is rendering, to add some margin at the bottom so the footer is not blocked
  isShowingCart?: boolean;
}

const FunnelFooterV2: React.FC<Props> = ({ isShowingCart = false }) => {
  const configContext = useNonNullContext(ConfigContext);
  const userContext = useContext(UserContext);
  const router = useRouter();
  const theme = useTheme();

  // If the user is logged in the footer will show the dashboard link, if not it will show login.
  const isLoggedIn = !!userContext?.user;
  // On the menu and checkout pages, the footer will not show the navigation button links.
  const isMenuOrCheckout =
    router.pathname === '/menu' || router.pathname === '/checkout';
  const showNavButtons = !isMenuOrCheckout;

  // Preserve all query params from the landing page when redirecting to the menu page.
  const handleMenuRedirect = () => {
    router.push({ pathname: '/menu', query: router.query });
  };

  const backgroundColor = theme.palette.common.white;
  const textColor = theme.palette.grey[600];

  const { termsOfUse, privacyPolicy } = configContext.globalLinks ?? {
    termsOfUse: undefined,
    privacyPolicy: undefined,
  };

  const footerButtonStyles = {
    borderColor: '#0000001A',
    color: '#000000',
    textTransform: 'none',
    borderRadius: '8px',
    fontWeight: 500,
    fontSize: '14px',
  };

  return (
    <Paper
      sx={{
        backgroundColor,
        color: textColor,
        borderTop: `1px solid ${theme.palette.grey[200]}`,
      }}
      square
      elevation={0}
    >
      <Box
        sx={{
          maxWidth: '1312px',
          margin: 'auto',
          padding: '16px',
          marginBottom: isShowingCart
            ? { xs: '3.5rem', md: '4rem' }
            : undefined,
        }}
      >
        {/* Top row: logo and buttons*/}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            padding: '16px 0',
            gap: { xs: '1rem', md: 0 },
          }}
        >
          <Logo />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: { xs: '0.5rem', md: '0.75rem' },
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Typography
                sx={{
                  color: textColor,
                  fontSize: '14px',
                }}
              >
                Need more info?
              </Typography>
              <ZendeskLoader
                sx={{
                  color: '#000000',
                  textTransform: 'none',
                  borderRadius: '8px',
                  fontWeight: 500,
                  fontSize: '14px',
                  border: '1px solid #0000001A',
                  padding: '4px 10px',
                }}
                loadingIndicator={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChatOutlinedIcon
                      fontSize="small"
                      sx={{ color: '#0000001A' }}
                    />
                    Loading...
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatOutlinedIcon
                    fontSize="small"
                    sx={{ color: '#000000' }}
                  />
                  Chat with us
                </Box>
              </ZendeskLoader>
            </Box>
            {showNavButtons && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMenuRedirect}
                  startIcon={
                    <MenuBookOutlinedIcon
                      fontSize="small"
                      sx={{ color: '#000000' }}
                    />
                  }
                  sx={footerButtonStyles}
                >
                  View menu
                </Button>
                {isLoggedIn ? (
                  <Button
                    variant="outlined"
                    size="small"
                    href={'/dashboard'}
                    startIcon={
                      <LoginOutlinedIcon
                        fontSize="small"
                        sx={{ color: '#000000' }}
                      />
                    }
                    sx={footerButtonStyles}
                  >
                    Go to Dashboard
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    href={'/login'}
                    startIcon={
                      <LoginOutlinedIcon
                        fontSize="small"
                        sx={{ color: '#000000' }}
                      />
                    }
                    sx={footerButtonStyles}
                  >
                    Login
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ margin: '1rem 0' }} />

        {/* Bottom row: Terms & Privacy links */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'flex-end' },
            padding: '8px 0',
          }}
        >
          <Box sx={{ display: 'flex', gap: { xs: '1rem', md: '1.5rem' } }}>
            {termsOfUse && (
              <Link
                href={termsOfUse.url}
                target={termsOfUse.target}
                underline="hover"
                sx={{ fontSize: '14px' }}
              >
                <Typography variant="body2" sx={{ color: textColor }}>
                  {termsOfUse.displayText}
                </Typography>
              </Link>
            )}
            {privacyPolicy && (
              <Link
                href={privacyPolicy.url}
                target={privacyPolicy.target}
                underline="hover"
                sx={{ fontSize: '14px' }}
              >
                <Typography variant="body2" sx={{ color: textColor }}>
                  {privacyPolicy.displayText}
                </Typography>
              </Link>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default FunnelFooterV2;
